class OSARTheme {
  constructor() {
    this.bindMenu();
    this.bindConfigurator();
    this.bindPricing();
    this.bindQuoteBuilder();
    this.bindAccordions();
    this.bindScrollReveal();
    this.bindStickyCTA();
  }

  bindMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      menu.hidden = open;
      document.body.classList.toggle('menu-open', !open);
    });
    menu.addEventListener('click', (event) => {
      if (event.target.matches('a')) {
        toggle.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
        document.body.classList.remove('menu-open');
      }
    });
  }

  bindConfigurator() {
    document.querySelectorAll('[data-configurator]').forEach((root) => {
      const can = root.querySelector('[data-preview-can]');
      const name = root.querySelector('[data-preview-name]');
      const detail = root.querySelector('[data-preview-detail]');
      const update = () => {
        const water = root.querySelector('[name="preview-water"]:checked')?.value || 'Still';
        const size = root.querySelector('[name="preview-size"]:checked')?.value || '355 mL standard';
        const colour = root.querySelector('[name="preview-colour"]:checked')?.value || '#1E66F5';
        const brand = root.querySelector('[data-brand-input]')?.value.trim() || 'YOUR BRAND';
        can.style.setProperty('--can-colour', colour);
        can.classList.toggle('is-sparkling', water === 'Sparkling');
        can.classList.toggle('is-sleek', size.includes('sleek'));
        can.classList.toggle('is-473', size.startsWith('473'));
        name.textContent = brand.slice(0, 20).toUpperCase();
        detail.textContent = `${water} water · ${size}`;
      };
      root.addEventListener('input', update);
      const artworkInput = root.querySelector('[data-artwork-input]');
      if (artworkInput) {
        artworkInput.addEventListener('change', async () => {
          const file = artworkInput.files?.[0];
          if (!file) return;
          const status = root.querySelector('[data-artwork-status]');
          const maxBytes = 20 * 1024 * 1024;
          if (file.size > maxBytes) {
            artworkInput.value = '';
            if (status) status.textContent = 'Please choose a file smaller than 20 MB.';
            return;
          }
          if (status) status.textContent = `Preparing ${file.name}…`;
          try {
            const artworkUrl = await this.artworkPreviewUrl(file, root);
            can.style.setProperty('--artwork-image', `url("${artworkUrl}")`);
            can.classList.add('has-artwork');
            root.querySelectorAll('[data-artwork-file-name]').forEach(node => { node.textContent = file.name; });
            document.querySelectorAll('[data-artwork-name-input]').forEach(node => { node.value = file.name; });
            const uploadedChoice = document.querySelector('[name="contact[Artwork status]"][value="Artwork uploaded in label studio"]');
            if (uploadedChoice) uploadedChoice.checked = true;
            if (status) status.textContent = 'Artwork preview applied. Final colour and placement are confirmed during proofing.';
          } catch (error) {
            console.error(error);
            if (status) status.textContent = 'We could not preview that file. Try a print PDF, PNG, JPG, WebP or SVG.';
          }
        });
      }
      root.querySelector('[data-remove-artwork]')?.addEventListener('click', () => {
        if (artworkInput) artworkInput.value = '';
        can.classList.remove('has-artwork');
        can.style.removeProperty('--artwork-image');
        root.querySelectorAll('[data-artwork-file-name]').forEach(node => { node.textContent = 'No file selected'; });
        const status = root.querySelector('[data-artwork-status]');
        if (status) status.textContent = 'PDF, PNG, JPG, WebP or SVG · maximum 20 MB';
        document.querySelectorAll('[data-artwork-name-input]').forEach(node => { node.value = ''; });
        const uploadedChoice = document.querySelector('[name="contact[Artwork status]"][value="Artwork uploaded in label studio"]');
        if (uploadedChoice) uploadedChoice.checked = false;
      });
      update();
    });
  }

  async artworkPreviewUrl(file, root) {
    if (file.type.startsWith('image/') || /\.(png|jpe?g|webp|svg)$/i.test(file.name)) {
      return URL.createObjectURL(file);
    }
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      const moduleUrl = root.dataset.pdfModule;
      const workerUrl = root.dataset.pdfWorker;
      if (!moduleUrl || !workerUrl) throw new Error('PDF preview module unavailable');
      const pdfjs = await import(moduleUrl);
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
      const page = await pdf.getPage(1);
      const initial = page.getViewport({ scale: 1 });
      const scale = Math.min(2, 1600 / initial.width);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d', { alpha: false });
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL('image/jpeg', .9);
    }
    throw new Error('Unsupported artwork type');
  }

  bindPricing() {
    document.querySelectorAll('[data-pricing-calculator]').forEach((root) => {
      const input = root.querySelector('[data-quantity-input]');
      const unit = root.querySelector('[data-unit-price]');
      const fee = root.querySelector('[data-small-fee]');
      const total = root.querySelector('[data-estimated-total]');
      const tier = root.querySelector('[data-price-tier]');
      const currency = root.dataset.currency || 'CAD';
      if (!input) return;
      if (!input.value || Number.parseInt(input.value, 10) < 1000) input.value = '1000';
      const money = value => new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(value);
      const calculate = () => {
        const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
        let unitPrice = null;
        let tierLabel = 'Enter at least 1,000 cans';
        if (quantity >= 1000 && quantity <= 2400) { unitPrice = 1.50; tierLabel = '1,000–2,400 tier'; }
        if (quantity >= 2401 && quantity <= 4799) { unitPrice = 1.25; tierLabel = '2,401–4,799 tier'; }
        if (quantity >= 4800) { unitPrice = .99; tierLabel = '4,800+ volume tier'; }
        const smallRunFee = quantity >= 1000 && quantity < 4800 ? 1000 : 0;
        unit.textContent = unitPrice === null ? 'Custom' : `${money(unitPrice)} / can`;
        fee.textContent = quantity < 1000 ? '—' : smallRunFee ? money(smallRunFee) : money(0);
        total.textContent = quantity < 1000 ? 'Minimum 1,000 cans' : unitPrice === null ? 'Custom quote' : money(quantity * unitPrice + smallRunFee);
        tier.textContent = tierLabel;
        root.classList.toggle('is-below-minimum', quantity > 0 && quantity < 1000);
        document.querySelectorAll('[data-quantity-sync]').forEach(node => { node.value = quantity >= 1000 ? String(quantity) : ''; });
      };
      input.addEventListener('input', calculate);
      root.querySelectorAll('[data-quantity-change]').forEach(button => {
        button.addEventListener('click', () => {
          const delta = Number.parseInt(button.dataset.quantityChange, 10) || 0;
          input.value = String(Math.max(1000, (Number.parseInt(input.value, 10) || 1000) + delta));
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
      calculate();
    });
  }

  bindQuoteBuilder() {
    document.querySelectorAll('[data-quote-builder]').forEach((root) => {
      const form = root.querySelector('form');
      if (!form) return;
      const panels = [...root.querySelectorAll('[data-step]')];
      const progress = root.querySelector('[data-progress]');
      const label = root.querySelector('[data-step-label]');
      let current = 0;

      const show = (index, shouldScroll = false) => {
        current = Math.max(0, Math.min(index, panels.length - 1));
        panels.forEach((panel, i) => { panel.hidden = i !== current; });
        if (progress) progress.style.width = `${((current + 1) / panels.length) * 100}%`;
        if (label) label.textContent = `Step ${current + 1} of ${panels.length}`;
        if (shouldScroll) root.closest('[data-quote-card]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.updateSummary(form);
      };

      form.addEventListener('click', (event) => {
        const next = event.target.closest('[data-next]');
        const back = event.target.closest('[data-back]');
        if (next) {
          const panel = panels[current];
          const required = [...panel.querySelectorAll('[required]')];
          const radioGroups = [...new Set(required.filter(el => el.type === 'radio').map(el => el.name))];
          const missingRadio = radioGroups.some(group => !panel.querySelector(`[name="${CSS.escape(group)}"]:checked`));
          const missingField = required.filter(el => el.type !== 'radio').some(el => !el.checkValidity());
          if (missingRadio || missingField) {
            const first = required.find(el => (el.type === 'radio' && !panel.querySelector(`[name="${CSS.escape(el.name)}"]:checked`)) || (el.type !== 'radio' && !el.checkValidity()));
            first?.focus();
            panel.classList.add('show-errors');
            return;
          }
          show(current + 1, true);
        }
        if (back) show(current - 1, true);
      });
      form.addEventListener('input', () => this.updateSummary(form));
      form.addEventListener('submit', async (event) => {
        if (form.dataset.nativeSubmitting === 'true') return;
        const artworkInput = document.querySelector('[data-artwork-input]');
        const file = artworkInput?.files?.[0];
        const variantId = root.dataset.uploadVariant;
        if (!file || !variantId) return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        const originalLabel = submit?.textContent;
        if (submit) { submit.disabled = true; submit.textContent = 'Uploading artwork…'; }
        try {
          const upload = new FormData();
          upload.append('id', variantId);
          upload.append('quantity', '1');
          upload.append('properties[Label artwork]', file, file.name);
          upload.append('properties[Project quantity]', form.querySelector('[name="contact[Quantity]"]')?.value || '');
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body: upload, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Artwork upload failed');
          const item = await response.json();
          const rawUrl = item.properties?.['Label artwork'] || '';
          const url = rawUrl.startsWith('/') ? `${window.location.origin}${rawUrl}` : rawUrl;
          const urlInput = form.querySelector('[data-artwork-url-input]');
          if (urlInput) urlInput.value = url;
          if (item.key) {
            await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
          }
          form.dataset.nativeSubmitting = 'true';
          form.submit();
        } catch (error) {
          console.error(error);
          if (submit) { submit.disabled = false; submit.textContent = originalLabel || 'Send my project'; }
          const notice = root.querySelector('[data-upload-error]');
          if (notice) { notice.hidden = false; notice.focus(); }
        }
      });
      show(0);
    });
  }

  updateSummary(form) {
    const value = (name) => form.querySelector(`[name="${CSS.escape(name)}"]:checked`)?.value || form.querySelector(`[name="${CSS.escape(name)}"]`)?.value || '—';
    form.querySelectorAll('[data-summary]').forEach((node) => {
      node.textContent = value(node.dataset.summary);
    });
    const payload = [
      `Water: ${value('contact[Water type]')}`,
      `Can size: ${value('contact[Can size]')}`,
      `Quantity: ${value('contact[Quantity]')}`,
      `Use: ${value('contact[Project type]')}`,
      `Need-by: ${value('contact[Need-by date]')}`,
      `Artwork: ${value('contact[Artwork status]')}`,
      `Delivery: ${value('contact[Delivery location]')}`
    ].join('\n');
    const body = form.querySelector('[data-project-summary]');
    if (body) body.value = payload;
  }

  bindAccordions() {
    document.querySelectorAll('[data-accordion-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('[data-accordion-item]');
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        item.classList.toggle('is-open', !open);
        item.querySelector('[data-accordion-panel]').hidden = open;
      });
    });
  }

  bindScrollReveal() {
    const selectors = [
      '.section-heading', '.pricing-heading', '.source-copy', '.source-visual',
      '.solution-card', '.showcase-heading', '.showcase-item', '.process-card', '.industry-card', '.quality-item',
      '.price-card', '.calculator-shell', '.studio-step', '.artwork-specs',
      '.design-help-card', '.quote-shell'
    ].join(',');
    const items = [...document.querySelectorAll(selectors)];
    items.forEach((item, index) => {
      item.classList.add('reveal');
      item.dataset.revealDelay = String(index % 4);
    });
    if (!('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .1, rootMargin: '0px 0px -45px' });
    items.forEach(item => observer.observe(item));
  }

  bindStickyCTA() {
    const cta = document.querySelector('[data-mobile-sticky-cta]');
    const quote = document.querySelector('#quote');
    if (!cta) return;
    const update = () => {
      const beforeQuote = !quote || window.scrollY < quote.offsetTop - window.innerHeight * .45;
      cta.classList.toggle('is-visible', window.scrollY > 620 && beforeQuote);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
}

document.addEventListener('DOMContentLoaded', () => new OSARTheme());
